import { Entity, generateEntityId } from './entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';

export class OilBarrel implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;
  isGrounded = true;

  health = 1;
  explosionRadius = 50;
  maxDamage = 50;

  onExplode?: (x: number, y: number) => void;

  private static readonly WIDTH = 28;
  private static readonly HEIGHT = 50;

  constructor(x: number, y: number) {
    this.id = generateEntityId('barrel');
    this.position = { x, y };
  }

  update(deltaTime: number): void {
    if (!this.active) return;

    if (!this.isGrounded) {
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
    } else {
      this.velocity.x *= 0.9;
      if (Math.abs(this.velocity.x) < 1) {
        this.velocity.x = 0;
      }
    }
  }

  applyTerrainPhysics(terrain: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active) return;

    const halfW = OilBarrel.WIDTH / 2;
    const halfH = OilBarrel.HEIGHT / 2;
    const quarterH = OilBarrel.HEIGHT / 4;

    if (this.velocity.x !== 0) {
      const sideX = this.velocity.x > 0 ? this.position.x + halfW : this.position.x - halfW;
      const hitsSide =
        terrain.isSolid(sideX, this.position.y - halfH + 2) ||
        terrain.isSolid(sideX, this.position.y) ||
        terrain.isSolid(sideX, this.position.y + halfH - 2);
      if (hitsSide) {
        while (this.velocity.x > 0 && terrain.isSolid(this.position.x + halfW, this.position.y)) {
          this.position.x -= 1;
        }
        while (this.velocity.x < 0 && terrain.isSolid(this.position.x - halfW, this.position.y)) {
          this.position.x += 1;
        }
        this.velocity.x = -this.velocity.x * 0.3;
      }
    }

    const feetY = this.position.y + quarterH;

    if (this.velocity.y >= 0) {
      const groundY = this.findGroundBelow(this.position.x, feetY, terrain);
      if (groundY !== null && groundY - feetY <= 2) {
        this.position.y = groundY - quarterH;
        this.velocity.y = 0;
        this.isGrounded = true;
        return;
      }
    }

    const hasGroundBelow =
      terrain.isSolid(this.position.x - halfW + 2, feetY + 1) ||
      terrain.isSolid(this.position.x, feetY + 1) ||
      terrain.isSolid(this.position.x + halfW - 2, feetY + 1);
    if (!hasGroundBelow) {
      this.isGrounded = false;
    }
  }

  private findGroundBelow(
    x: number,
    startY: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): number | null {
    for (let dy = 0; dy <= 5; dy++) {
      if (terrain.isSolid(x, startY + dy)) {
        return startY + dy;
      }
    }
    return null;
  }

  applyExplosionForce(pushX: number, pushY: number): void {
    this.velocity.x += pushX * 0.5;
    this.velocity.y += pushY * 0.5;
    this.isGrounded = false;
  }

  takeDamage(_amount: number): void {
    if (!this.active) return;
    this.explode();
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x, this.position.y);
  }

  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x - OilBarrel.WIDTH / 2,
      y: this.position.y - OilBarrel.HEIGHT / 2,
      width: OilBarrel.WIDTH,
      height: OilBarrel.HEIGHT,
    };
  }

  destroy(): void {
    this.active = false;
  }
}
